import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { liveObject } from '../../../../core/i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BoardColumn, BoardField, BoardManager } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { TranslateService } from '@ngx-translate/core';
import { BoardV3RealtimeService } from '../../board-v3-realtime.service';

/** Clients block of the quick create form (contacts and the company); it is not a funnel field, so it is always offered */
export const QUICK_CREATE_CLIENTS_KEY = 'clients';

@Component({
  selector: 'app-board-v3-quick-create-settings',
  templateUrl: './quick-create-settings.component.html',
  styleUrls: ['./quick-create-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QuickCreateSettingsComponent implements OnInit, OnChanges {
  private readonly translateService = inject(TranslateService);
  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeService = inject(BoardV3RealtimeService);

  @Input() funnelId: string | null = null;
  @Input() activeKeys: string[] = [];
  @Input() managers: BoardManager[] = [];

  @Output() apply = new EventEmitter<string[]>();
  @Output() close = new EventEmitter<{ changed: boolean } | undefined>();

  readonly clientsKey = QUICK_CREATE_CLIENTS_KEY;
  enabledFields = new Set<string>();
  previewKeys: string[] = [];
  fields: BoardField[] = [];

  previewColumn: BoardColumn = liveObject(() => ({
    id: 'preview',
    active: true,
    created_by: null,
    created_at: '',
    updated_by: null,
    updated_at: null,
    deleted_by: null,
    deleted_at: null,
    company_id: '',
    funnel_id: '',
    name: this.translateService.instant('board_v3_preview'),
    color: { text: '#000000', background: '#ffffff' },
    position: 0,
    type: 'DEFAULT',
    count_cards: '0',
    currency: 'UAH',}));

  ngOnInit(): void {
    this.enabledFields = new Set(this.activeKeys ?? []);
    this.syncPreview();
    this.loadFields();
    this.subscribeToRealtime();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeKeys'] && !changes['activeKeys'].firstChange) {
      this.enabledFields = new Set(this.activeKeys ?? []);
      this.syncPreview();
    }

    if (changes['funnelId'] && !changes['funnelId'].firstChange) this.loadFields();
  }

  private loadFields(): void {
    if (!this.funnelId) return;

    this.boardService
      .getBoardFields(this.funnelId, 'FUNNEL')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fields) => {
          this.fields = fields;
          this.syncPreview();
        },
      });
  }

  private subscribeToRealtime(): void {
    if (!this.funnelId) return;

    this.realtimeService
      .fieldsUpdated$(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.fields = event.fields ?? [];
        if (event.funnel?.create_fields) {
          this.enabledFields = new Set(event.funnel.create_fields);
        }
        this.syncPreview();
      });

    this.realtimeService
      .funnelUpdatedById$(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ funnel }) => {
        this.enabledFields = new Set(funnel?.create_fields ?? []);
        this.syncPreview();
      });
  }

  get totalCount(): number {
    return this.fields.length + 1;
  }

  get enabledCount(): number {
    return this.getEnabledKeys().length;
  }

  isEnabled(key: string): boolean {
    return this.enabledFields.has(key);
  }

  setEnabled(key: string, value: boolean): void {
    if (value) this.enabledFields.add(key);
    else this.enabledFields.delete(key);
    this.syncPreview();
  }

  private syncPreview(): void {
    this.previewKeys = this.getEnabledKeys();
  }

  private getEnabledKeys(): string[] {
    const fieldKeys = this.fields.filter((f) => this.isEnabled(f.identifier)).map((f) => f.identifier);
    return this.isEnabled(this.clientsKey) ? [this.clientsKey, ...fieldKeys] : fieldKeys;
  }

  onCancelClick(): void {
    this.close.emit();
  }

  onApplyClick(): void {
    this.apply.emit(this.getEnabledKeys());
  }
}
